import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useNavigate } from "react-router";
import { API_URL } from "../../api/auth";
import { useApiQuery } from "../../hooks/use-api-query";
import { useClerkToken } from "../../hooks/use-clerk-token";

interface OAuthIntegrationStatus {
  connected: boolean;
  provider: string;
  scopes?: string;
  metadata?: {
    team_name?: string;
    team_id?: string;
  };
  created_at?: string;
}

interface IntegrationCardProps {
  name: string;
  provider: string;
  description: string;
  logo: string;
  enabled: boolean;
  comingSoon?: boolean;
  configureUrl?: string;
}

function IntegrationCard({
  name,
  provider,
  logo,
  enabled,
  comingSoon = false,
  configureUrl,
}: IntegrationCardProps) {
  const { token } = useClerkToken();
  const { enqueueSnackbar } = useSnackbar();
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const navigate = useNavigate();

  const {
    data: status,
    isLoading,
    refetch,
  } = useApiQuery<OAuthIntegrationStatus>(`/oauth/${provider}/status`, {
    enabled: enabled && !comingSoon,
  });

  const handleConnect = async () => {
    if (!token) {
      enqueueSnackbar("Please log in to connect integrations", {
        variant: "error",
      });
      return;
    }

    try {
      // Fetch the OAuth URL from backend with auth header
      const response = await fetch(`${API_URL}/oauth/${provider}/authorize`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json", // Request JSON response instead of redirect
        },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const detail = body?.detail;
        const message =
          typeof detail === "string"
            ? detail
            : (body?.message ?? `HTTP error! status: ${response.status}`);
        throw new Error(message);
      }

      // Parse JSON response with authorization URL
      const data = await response.json();

      if (data.authorization_url) {
        // Redirect to OAuth provider page
        window.location.href = data.authorization_url;
      } else {
        throw new Error("No authorization URL in response");
      }
    } catch (error) {
      console.error("Failed to initiate OAuth:", error);
      enqueueSnackbar(`Failed to connect ${name}. Please try again.`, {
        variant: "error",
      });
    }
  };

  const handleDisconnect = async () => {
    if (!token) return;

    setIsDisconnecting(true);
    try {
      const response = await fetch(`${API_URL}/oauth/${provider}/disconnect`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to disconnect");
      }

      enqueueSnackbar(`${name} disconnected successfully`, {
        variant: "success",
      });
      refetch();
    } catch (error) {
      console.error("Failed to disconnect:", error);
      enqueueSnackbar(`Failed to disconnect ${name}`, {
        variant: "error",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const isConnected = status?.connected ?? false;

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        p: 3,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: 1,
        },
      }}
    >
      <Stack direction="column" spacing={2} alignItems="center" width="100%">
        <Box
          component="img"
          src={logo}
          alt={`${name} logo`}
          sx={{
            width: 64,
            height: 64,
            objectFit: "contain",
            filter: comingSoon ? "grayscale(100%) opacity(0.5)" : "none",
            transition: "filter 0.2s ease-in-out",
          }}
        />
        <Typography variant="h6" textAlign="center">
          {name}
        </Typography>
        {isConnected && <Chip label="Connected" color="success" size="small" />}
      </Stack>

      <Box sx={{ width: "100%", mt: 2 }}>
        {isConnected ? (
          <Stack spacing={1}>
            {configureUrl && (
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate(configureUrl)}
                startIcon={<SettingsIcon />}
              >
                Configure
              </Button>
            )}
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleConnect}
                disabled={isDisconnecting || isLoading}
              >
                {isLoading ? <CircularProgress size={20} /> : "Reconnect"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={handleDisconnect}
                disabled={isDisconnecting}
              >
                {isDisconnecting ? <CircularProgress size={20} /> : "Disconnect"}
              </Button>
            </Stack>
          </Stack>
        ) : comingSoon ? (
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              const subject = encodeURIComponent(`Request for ${name} Integration`);
              const body = encodeURIComponent(
                `Hi,\n\nI would like to request the ${name} integration for desplega.ai.\n\nThank you!`,
              );
              window.location.href = `mailto:contact@desplega.ai?subject=${subject}&body=${body}`;
            }}
          >
            Request
          </Button>
        ) : (
          <Button
            variant="contained"
            fullWidth
            onClick={handleConnect}
            disabled={!enabled || isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : "Connect"}
          </Button>
        )}
      </Box>
    </Card>
  );
}

export function IntegrationsTab() {
  return (
    <Card>
      <CardContent>
        <Stack direction="column" spacing={3}>
          <Box>
            <Typography variant="h5" component="div" gutterBottom>
              Integrations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Connect your favorite tools to enhance your workflow
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <IntegrationCard
                name="Slack"
                provider="slack"
                description="Run tests, get notifications, and manage your QA workflow directly from Slack"
                logo="/slack.png"
                enabled={true}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <IntegrationCard
                name="Linear"
                provider="linear"
                description="Create issues from test failures and sync test results with Linear"
                logo="/linear-icon.png"
                enabled={false}
                comingSoon={true}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <IntegrationCard
                name="GitHub"
                provider="github"
                description="Create PRs and link tests to your code repositories"
                logo="/github-mark.png"
                enabled={true}
                configureUrl="/integrations/github/setup-repos"
              />
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
}
